/**
 * StandardPageHeader
 *
 * Standardized page header component with automatic breadcrumbs, icon, title, and subtitle.
 * Provides consistent styling across all pages with gradient text, animations, and responsive design.
 *
 * Features:
 * - Automatic breadcrumb generation from URL
 * - Module icon display with optional override
 * - Animated entrance effects
 * - Gradient title styling
 * - Responsive layout
 *
 * @example
 * ```tsx
 * <StandardPageHeader
 *   title="Teams Management"
 *   subtitle="Search, filter, and manage all teams across your organization"
 *   icon="Teams" // Optional - auto-detected from URL if not provided
 * />
 * ```
 *
 * @see src/hooks/useBreadcrumbs.tsx - Breadcrumb generation logic
 * @see src/constants/moduleIcons.tsx - Icon registry
 */

import React from 'react';
import {
  Box,
  Typography,
  alpha,
  useTheme,
  Breadcrumbs as MuiBreadcrumbs,
  Link,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { useBreadcrumbs } from 'hooks/useBreadcrumbs';
import { getModuleIcon } from '../constants/moduleIcons';

/* ============================================================================
 * TYPES
 * ========================================================================== */

export interface StandardPageHeaderProps {
  /** Page title (required) */
  title: string;
  /** Subtitle/description (optional) */
  subtitle?: string;
  /** Module icon key from moduleIcons.tsx (optional - auto-detected from URL if not provided) */
  icon?: string;
  /** Whether to show breadcrumbs (default: true) */
  showBreadcrumbs?: boolean;
  /** Whether to show the module icon (default: true) */
  showIcon?: boolean;
  /** Custom icon element (overrides icon prop) */
  customIcon?: React.ReactNode;
  /** Custom breadcrumbs to override automatic generation */
  customBreadcrumbs?: Array<{ label: string; path?: string }>;
  /** Content to render after the title (e.g., status badge) */
  titleSuffix?: React.ReactNode;
  /** Additional styles for the container */
  sx?: any;
}

/* ============================================================================
 * COMPONENT
 * ========================================================================== */

/**
 * StandardPageHeader Component
 *
 * Provides a consistent, polished header for all pages with:
 * - Breadcrumb navigation
 * - Module icon
 * - Gradient title text
 * - Animated entrance
 * - Subtitle text
 */
export const StandardPageHeader: React.FC<StandardPageHeaderProps> = ({
  title,
  subtitle,
  icon,
  showBreadcrumbs = true,
  showIcon = true,
  customIcon,
  customBreadcrumbs,
  titleSuffix,
  sx,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const autoBreadcrumbs = useBreadcrumbs();
  const breadcrumbs = customBreadcrumbs || autoBreadcrumbs;

  // Determine which icon to display
  const displayIcon = React.useMemo(() => {
    if (customIcon) return customIcon;
    if (!showIcon) return null;

    // Use provided icon key or try to get from breadcrumbs
    const iconKey = icon || breadcrumbs[breadcrumbs.length - 1]?.label;
    if (!iconKey) return null;

    return getModuleIcon(iconKey, 'pageHeader');
  }, [customIcon, showIcon, icon, breadcrumbs]);

  return (
    <Box
      mb={4}
      sx={{
        ...sx,
        // Ensure consistent background during scroll (prevents white flash)
        backgroundColor: 'transparent',
      }}
    >
      {/* Breadcrumbs */}
      {showBreadcrumbs && breadcrumbs.length > 0 && (
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: 0 }}
        >
          <MuiBreadcrumbs
            separator={<ChevronRight size={14} opacity={0.5} />}
            maxItems={8} // Prevents overflow; collapse breadcrumbs if >8
            sx={{
              mb: 2,
              '& .MuiBreadcrumbs-separator': {
                mx: 0.5,
              },
            }}
          >
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1;
              const isHome = index === 0;

              return isLast ? (
                <Typography
                  key={crumb.path || crumb.label || index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: theme.palette.text.primary,
                    fontSize: '0.875rem',
                    fontWeight: 600,
                  }}
                >
                  {isHome && <Home size={14} />}
                  {crumb.label}
                </Typography>
              ) : (
                <Link
                  key={crumb.path || crumb.label || index}
                  underline="hover"
                  onClick={(e) => {
                    e.preventDefault();
                    if (crumb.path) navigate(crumb.path);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: alpha(theme.palette.text.secondary, 0.7),
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'color 0.2s ease',
                    '&:hover': {
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  {isHome && <Home size={14} />}
                  {crumb.label}
                </Link>
              );
            })}
          </MuiBreadcrumbs>
        </motion.div>
      )}

      {/* Title Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: showBreadcrumbs ? 0.1 : 0 }}
      >
        <Box display="flex" alignItems="center" gap={2} mb={subtitle ? 1 : 0}>
          {/* Icon */}
          {displayIcon && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                borderRadius: '12px',
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(
                  theme.palette.secondary.main,
                  0.1,
                )})`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                color: theme.palette.primary.main,
                '& svg': {
                  width: 28,
                  height: 28,
                },
              }}
            >
              {displayIcon}
            </Box>
          )}

          {/* Title */}
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '-0.02em',
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>

          {/* Title Suffix (e.g., status badge) */}
          {titleSuffix}
        </Box>

        {/* Subtitle */}
        {subtitle && (
          <Typography
            variant="body1"
            sx={{
              color: alpha(theme.palette.text.secondary, 0.8),
              fontWeight: 500,
              ml: displayIcon ? '64px' : 0, // Align with title when icon is present
            }}
          >
            {subtitle}
          </Typography>
        )}
      </motion.div>
    </Box>
  );
};

export default StandardPageHeader;
